import { getBestTime } from '../utils/bestTimes';
import { GlobalBestRecord } from '../firebase/leaderboard';

export interface LevelCompleteInfo {
  level: number;
  timeMs: number;
  isNewBest: boolean;
  isNewWorldRecord?: boolean;
}

export class LevelSelectScreen {
  private overlay: HTMLDivElement;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;
  private onSelect: ((level: number) => void) | null = null;
  private maxLevel: number = 1;
  private selectedLevel: number = 1;
  private completionInfo: LevelCompleteInfo | null = null;
  private globalBests: Map<number, GlobalBestRecord> = new Map();

  constructor() {
    this.overlay = document.createElement('div');
    this.overlay.id = 'level-select-screen';
    this.overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
      display: none;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 1000;
      font-family: monospace;
      color: #fff;
    `;

    document.body.appendChild(this.overlay);
  }

  show(
    maxLevel: number,
    onSelect: (level: number) => void,
    completionInfo?: LevelCompleteInfo,
    globalBests?: Map<number, GlobalBestRecord>
  ): void {
    this.maxLevel = maxLevel;
    this.selectedLevel = maxLevel; // Default to first unplayed level
    this.onSelect = onSelect;
    this.completionInfo = completionInfo || null;
    this.globalBests = globalBests || new Map();

    this.render();
    this.overlay.style.display = 'flex';

    // Set up keyboard handler
    this.keyHandler = (e: KeyboardEvent) => {
      if (e.code === 'ArrowUp' || e.code === 'KeyW') {
        this.selectedLevel = Math.min(this.selectedLevel + 1, this.maxLevel);
        this.render();
      } else if (e.code === 'ArrowDown' || e.code === 'KeyS') {
        this.selectedLevel = Math.max(this.selectedLevel - 1, 1);
        this.render();
      } else if (e.code === 'Enter' || e.code === 'Space') {
        this.hide();
        if (this.onSelect) this.onSelect(this.selectedLevel);
      }
    };
    document.addEventListener('keydown', this.keyHandler);
  }

  private render(): void {
    // Preserve scroll position before re-rendering
    const existingList = this.overlay.querySelector('#level-list') as HTMLElement;
    const savedScrollTop = existingList ? existingList.scrollTop : 0;

    let html = '';

    // Show completion info if this is after finishing a level
    if (this.completionInfo) {
      const timeStr = this.formatTime(this.completionInfo.timeMs);
      html += `
        <h1 style="font-size: 48px; margin-bottom: 20px; color: #ffd700;">Level Complete!</h1>
        <p style="font-size: 24px; margin-bottom: 10px;">Level ${this.completionInfo.level} completed in ${timeStr}</p>
        <p style="font-size: 20px; margin-bottom: 30px; color: #ff0; height: 24px;">
          ${this.completionInfo.isNewWorldRecord ? '** NEW WORLD RECORD! **' : this.completionInfo.isNewBest ? '** New Personal Best! **' : ''}
        </p>
      `;
    } else {
      html += `
        <h1 style="font-size: 48px; margin-bottom: 30px; color: #ffd700;">Derelict</h1>
      `;
    }

    html += `
      <div style="font-size: 18px; margin-bottom: 20px; color: #aaa;">
        W/S or Arrows to select, Enter to start
      </div>
      <div id="level-list" style="display: flex; flex-direction: column; gap: 8px; max-height: 60vh; overflow-y: auto; padding: 10px 20px 10px 10px;">
    `;

    // Show levels in reverse order (highest first)
    for (let level = this.maxLevel; level >= 1; level--) {
      const bestTime = getBestTime(level);
      const personalTimeStr = bestTime !== null ? this.formatTime(bestTime) : '---';
      const globalBest = this.globalBests.get(level);
      const isSelected = level === this.selectedLevel;
      const bgColor = isSelected ? 'rgba(255, 215, 0, 0.3)' : 'transparent';
      const borderColor = isSelected ? '#ffd700' : '#444';
      const arrow = isSelected ? '>' : ' ';

      let globalBestHtml = '';
      if (globalBest) {
        const globalTimeStr = this.formatTime(globalBest.time);
        globalBestHtml = `
          <div style="font-size: 12px; color: #f8f; margin-top: 2px;">
            Record: ${globalTimeStr} by ${this.escapeHtml(globalBest.playerName)}
          </div>
        `;
      }

      html += `
        <div data-level="${level}" style="
          padding: 10px 20px;
          background: ${bgColor};
          border: 2px solid ${borderColor};
          border-radius: 4px;
          min-width: 300px;
        ">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span>${arrow} Level ${level}</span>
            <span style="color: #8f8;">${personalTimeStr}</span>
          </div>
          ${globalBestHtml}
        </div>
      `;
    }

    html += `</div>`;
    this.overlay.innerHTML = html;

    // Restore scroll position and then adjust if needed
    const listEl = this.overlay.querySelector('#level-list') as HTMLElement;
    const selectedEl = this.overlay.querySelector(`[data-level="${this.selectedLevel}"]`) as HTMLElement;
    if (listEl && selectedEl) {
      // Restore previous scroll position first
      listEl.scrollTop = savedScrollTop;

      // Then check if selected item is visible and scroll only if needed
      const listRect = listEl.getBoundingClientRect();
      const selectedRect = selectedEl.getBoundingClientRect();

      if (selectedRect.top < listRect.top) {
        selectedEl.scrollIntoView({ block: 'start', behavior: 'instant' });
      } else if (selectedRect.bottom > listRect.bottom) {
        selectedEl.scrollIntoView({ block: 'end', behavior: 'instant' });
      }
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private formatTime(timeMs: number): string {
    const totalSeconds = timeMs / 1000;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const tenths = Math.floor((totalSeconds * 10) % 10);
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${tenths}`;
  }

  hide(): void {
    this.overlay.style.display = 'none';

    if (this.keyHandler) {
      document.removeEventListener('keydown', this.keyHandler);
      this.keyHandler = null;
    }
  }

  isVisible(): boolean {
    return this.overlay.style.display === 'flex';
  }

  destroy(): void {
    this.hide();
    this.overlay.remove();
  }
}
